/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { mapProps } from 'recompose';
import RowPrefix from 'app/components/commons/RowPrefix';
import t from 'app/utils/translate';
import { IN_PROGRESS } from 'app/constants/challengeStatusTypes';

const Entry = styled.div(({ theme }) => ({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  fontSize: theme.fontSize.tiny,
  fontWeight: theme.fontWeight.demiBold,
  lineHeight: 1,
  position: 'relative',
  width: 20
}));

const Closed = styled(Entry)(({ theme }) => ({
  backgroundColor: theme.color.basis2,
  color: theme.color.white
}));

const Entered = styled(Entry)(({ theme }) => ({
  backgroundColor: theme.color.primary,
  color: theme.color.white
}));

const Open = styled(Entry)(({ theme }) => ({
  color: theme.color.basis1
}));

const Border = styled.div(
  {
    position: 'absolute',
    height: '100%',
    right: 0,
    transform: 'scale(-1, 0.7)',
    width: 1
  },
  ({ theme }) => ({
    backgroundColor: theme.color.basis6
  })
);

const Background = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  backgroundColor: 'inherit'
});

type OuterProps = {
  entryCap: number,
  entryCount: number,
  isUnlimited: boolean,
  maxEntriesPerPerson: number,
  statusId: number,
  userEntryCount: number
};

type InnerProps = {
  component: any,
  text: string
};

function ChallengeEntryState({ component: Component, text }: InnerProps) {
  return (
    <Component>
      <Border />
      <Background />
      <RowPrefix>{text}</RowPrefix>
    </Component>
  );
}

export function getChallengeEntryStatus({
  maxEntriesPerPerson,
  userEntryCount,
  statusId,
  entryCap,
  entryCount,
  isUnlimited
}) {
  if (maxEntriesPerPerson === 1 && userEntryCount === 1) {
    return 'entered';
  }

  if (userEntryCount > 0) {
    return 'multi_entered';
  }

  if (statusId < IN_PROGRESS && (!entryCap || entryCount < entryCap || isUnlimited)) {
    return 'open';
  }

  return 'closed';
}

function propsMapper({
  entryCap,
  entryCount,
  isUnlimited,
  maxEntriesPerPerson,
  statusId,
  userEntryCount
}: OuterProps): InnerProps {
  const status = getChallengeEntryStatus({
    maxEntriesPerPerson,
    userEntryCount,
    statusId,
    entryCap,
    entryCount,
    isUnlimited
  });

  switch (status) {
    case 'entered':
      return {
        component: Entered,
        text: 'Entered'
      };

    case 'multi_entered':
      return {
        component: Entered,
        text: `ENT ${userEntryCount}/${maxEntriesPerPerson}`
      };

    case 'open':
      return {
        component: Open,
        text: t('Open')
      };

    default:
      return {
        component: Closed,
        text: t('Closed')
      };
  }
}

export default mapProps(propsMapper)(ChallengeEntryState);
